import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, MapPin, Bed, Bath, Maximize, Eye, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface TopRatedProperty {
  id: string;
  title: string;
  address: string;
  monthly_price: number;
  type: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  average_rating: number;
  total_ratings: number;
  views: number;
  property_images: {
    url: string;
    is_main: boolean;
  }[];
}

const TopRatedProperties = () => {
  const { data: topProperties = [], isLoading } = useQuery({
    queryKey: ['top-rated-properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          id, title, address, monthly_price, type, bedrooms, bathrooms, area, average_rating, total_ratings, views,
          property_images(url, is_main)
        `)
        .eq('is_active', true)
        .order('average_rating', { ascending: false, nullsFirst: false })
        .order('total_ratings', { ascending: false, nullsFirst: false })
        .order('views', { ascending: false })
        .limit(3);

      if (error) throw error;
      
      // ترتيب النتائج بناءً على معادلة تأخذ في الاعتبار التقييم وعدد المقيمين والمشاهدات
      const sortedData = (data || []).sort((a, b) => {
        const scoreA = (a.average_rating || 0) * 0.5 + (a.total_ratings || 0) * 0.3 + (a.views || 0) * 0.0001;
        const scoreB = (b.average_rating || 0) * 0.5 + (b.total_ratings || 0) * 0.3 + (b.views || 0) * 0.0001;
        return scoreB - scoreA;
      });
      
      return sortedData as TopRatedProperty[];
    },
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">العقارات الأعلى تقييماً</h2>
            <p className="text-gray-500">اكتشف أفضل العقارات التي حصلت على أعلى التقييمات</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-60 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-6"></div>
                  <div className="flex space-x-4 mb-6">
                    <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                  </div>
                  <div className="h-10 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (topProperties.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">العقارات الأعلى تقييماً</h2>
            <p className="text-gray-500">اكتشف أفضل العقارات التي حصلت على أعلى التقييمات</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
            <div className="bg-gray-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <Star className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">لا توجد عقارات متاحة</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              لم نتمكن من العثور على عقارات مقيمة حالياً. يرجى التحقق مرة أخرى لاحقاً.
            </p>
            <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white">
              استكشاف جميع العقارات
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center text-sm px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full mb-4">
            <Star className="h-4 w-4 mr-1 fill-current" />
            العقارات الأفضل تقييماً
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">اكتشف أفضل العقارات المميزة</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            استكشف العقارات التي حصلت على أعلى التقييمات من المستأجرين. اختياراتنا المميزة تجمع بين الجودة والسعر المناسب.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {topProperties.map((property, index) => {
            const mainImage = property.property_images?.find(img => img.is_main) || property.property_images?.[0];
            
            return (
              <div 
                key={property.id} 
                className="group bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500"
              >
                {/* صورة العقار مع تأثيرات */}
                <div className="relative h-64 overflow-hidden">
                  {mainImage ? (
                    <div className="relative h-full w-full">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"></div>
                      <img 
                        src={mainImage.url} 
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-2 bg-gray-300/40 rounded-lg flex items-center justify-center">
                          <MapPin className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">لا توجد صورة متاحة</p>
                      </div>
                    </div>
                  )}
                  
                  {/* شارة التقييم */}
                  <div className="absolute top-4 left-4 z-20">
                    <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 border-0 shadow-lg flex items-center">
                      <Star className="h-4 w-4 mr-1 fill-amber-700" />
                      <span className="font-bold">{property.average_rating.toFixed(1)}</span>
                      <span className="ml-1">({property.total_ratings})</span>
                    </Badge>
                  </div>

                  {/* عدد المشاهدات */}
                  <div className="absolute top-4 right-4 z-20">
                    <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-gray-700">
                      <Eye className="h-4 w-4 mr-1" />
                      {property.views}
                    </Badge>
                  </div>
                  
                  {/* نوع العقار */}
                  <div className="absolute bottom-4 left-4 z-20">
                    <Badge className="bg-white/90 backdrop-blur-sm text-gray-700 font-medium">
                      {property.type}
                    </Badge>
                  </div>
                  
                  {/* السعر */}
                  <div className="absolute bottom-4 right-4 z-20">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-3 py-1 rounded-lg font-bold text-lg shadow-lg">
                      {property.monthly_price.toLocaleString('ar')} <span className="text-sm font-normal">ر.ع/شهرياً</span>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  {/* عنوان العقار */}
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                    {property.title}
                  </h3>

                  {/* الموقع */}
                  <div className="flex items-center text-gray-500 mb-5">
                    <MapPin className="h-5 w-5 mr-2 text-blue-500" />
                    <span className="text-sm line-clamp-1">{property.address}</span>
                  </div>

                  {/* معلومات العقار */}
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <Bed className="h-5 w-5 text-blue-500 mr-1" />
                        <span className="font-medium text-gray-700">{property.bedrooms}</span>
                      </div>
                      <span className="text-xs text-gray-500">غرف نوم</span>
                    </div>
                    
                    <div className="h-6 w-px bg-gray-200"></div>
                    
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <Bath className="h-5 w-5 text-blue-500 mr-1" />
                        <span className="font-medium text-gray-700">{property.bathrooms}</span>
                      </div>
                      <span className="text-xs text-gray-500">حمامات</span>
                    </div>
                    
                    <div className="h-6 w-px bg-gray-200"></div>
                    
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <Maximize className="h-5 w-5 text-blue-500 mr-1" />
                        <span className="font-medium text-gray-700">{property.area} م²</span>
                      </div>
                      <span className="text-xs text-gray-500">المساحة</span>
                    </div>
                  </div>

                  {/* زر عرض المزيد */}
                  <Link to={`/property/${property.id}`} className="block">
                    <Button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-300 group-hover:translate-y-[-2px] flex items-center justify-center">
                      عرض التفاصيل
                      <ChevronLeft className="h-4 w-4 mr-1" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link 
            to="/properties?sort=highest_rated"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 hover:text-blue-700 font-medium rounded-lg shadow-sm hover:shadow-md transition-all"
          >
            <span>استكشاف جميع العقارات المقيمة</span>
            <Star className="h-4 w-4 mr-2 fill-current" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopRatedProperties;